import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaDownload,
  FaEye,
  FaMapMarkerAlt,
  FaRedo,
  FaSpinner,
  FaThumbtack,
} from 'react-icons/fa';
import { Select } from '../../../components/forms/Select';
import { Textarea } from '../../../components/forms/Textarea';
import { DOCUMENT_TYPE_META } from '../constants';
import { usePasStore } from '../pasStore';
import type { PolicyDocument, PolicyNote, RiskFactor } from '../types';
import { formatCurrency, formatDate, relativeTime, triggerUrlDownload } from '../utils';
import { cn } from '../../../utils/cn';
import { Button } from '../components/Button';
import { PdfPreview } from '../components/PdfPreview';
import { SlideOver } from '../components/SlideOver';
import { Timeline } from '../components/Timeline';
import { WorkflowActions } from '../components/WorkflowActions';
import { WorkflowStepper } from '../components/WorkflowStepper';
import {
  DocumentTypeBadge,
  PolicyStatusBadge,
  RiskTierBadge,
  UnderwritingStatusBadge,
} from '../components/StatusBadges';
import { Card, DescriptionList, EmptyState, ProgressBar, SectionCard } from '../components/primitives';

type TabKey = 'overview' | 'coverage' | 'risk' | 'documents' | 'timeline' | 'notes';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'coverage', label: 'Coverage' },
  { key: 'documents', label: 'Documents' },
  { key: 'timeline', label: 'Timeline' },
  { key: 'notes', label: 'Notes' },
];

const RATING_COLOR: Record<RiskFactor['rating'], string> = {
  favorable: 'var(--color-success-600)',
  neutral: 'var(--color-text-secondary)',
  adverse: 'var(--color-error-600)',
};


export const PolicyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const policy = usePasStore((s) => s.policies.find((p) => p.id === id));
  const loaded = usePasStore((s) => s.loaded);
  const setNotePinned = usePasStore((s) => s.setNotePinned);
  const addNote = usePasStore((s) => s.addNote);
  const addDocumentVersion = usePasStore((s) => s.addDocumentVersion);

  const [tab, setTab] = useState<TabKey>('overview');
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [noteBody, setNoteBody] = useState('');
  const [noteCategory, setNoteCategory] = useState<PolicyNote['category']>('general');
  const [savingNote, setSavingNote] = useState(false);
  const [regenerating, setRegenerating] = useState<string | null>(null);

  if (!policy) {
    return (
      <Card>
        <EmptyState
          title={loaded ? 'Policy not found' : 'Loading policy…'}
          message={loaded ? 'This policy may have been created in a previous session (mock data resets on reload).' : undefined}
          action={loaded ? <Button onClick={() => navigate('/policy-admin/policies')}>Back to policies</Button> : undefined}
        />
      </Card>
    );
  }

  const previewDoc = policy.documents.find((d) => d.id === previewDocId) ?? null;
  const sortedNotes = [...policy.notes].sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.createdAt.localeCompare(a.createdAt));

  const handleAddNote = async () => {
    if (!noteBody.trim()) return;
    setSavingNote(true);
    try {
      await addNote(policy.id, noteBody.trim(), noteCategory);
      setNoteBody('');
    } catch {
      // The store surfaces the error as a toast.
    } finally {
      setSavingNote(false);
    }
  };

  const handleRegenerate = async (doc: PolicyDocument) => {
    setRegenerating(doc.id);
    try {
      await addDocumentVersion(policy.id, doc.id, 'Regenerated from Policy Administration');
    } catch {
      // The store surfaces the error as a toast.
    } finally {
      setRegenerating(null);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate('/policy-admin/policies')}
        className="mb-4 inline-flex items-center gap-2 text-sm font-medium hover:underline"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        <FaArrowLeft className="h-3 w-3" /> All policies
      </button>

      {/* Header */}
      <Card className="mb-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--color-text-primary)' }}>
                {policy.policyNumber}
              </h1>
              <PolicyStatusBadge status={policy.status} size="md" />
            </div>
            <p className="mt-1 text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {policy.insured.name}
            </p>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              {policy.product} · {policy.broker.agency}
              {policy.sourceQuote && ` · from quote “${policy.sourceQuote.quoteName}” (${policy.sourceQuote.variationLabel})`}
            </p>
          </div>
          <WorkflowActions policy={policy} />
        </div>

        <div className="mt-6 border-t pt-5" style={{ borderColor: 'var(--color-border-light)' }}>
          <WorkflowStepper status={policy.status} />
        </div>

        <div className="mt-5 grid grid-cols-2 gap-4 border-t pt-5 sm:grid-cols-3 lg:grid-cols-5" style={{ borderColor: 'var(--color-border-light)' }}>
          {[
            { label: 'Effective', value: formatDate(policy.effectiveDate) },
            { label: 'Expires', value: formatDate(policy.expirationDate) },
            { label: 'Annual premium', value: formatCurrency(policy.annualPremium) },
            { label: 'Carrier', value: policy.carrier.name },
            { label: 'Underwriting', value: <UnderwritingStatusBadge status={policy.underwriting.status} /> },
          ].map((item) => (
            <div key={item.label} className="min-w-0">
              <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
                {item.label}
              </p>
              <div className="mt-1 truncate text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Tabs */}
      <div className="mb-6 flex gap-1 overflow-x-auto border-b" style={{ borderColor: 'var(--color-border-light)' }}>
        {TABS.map((t) => {
          const active = tab === t.key;
          const count = t.key === 'documents' ? policy.documents.length : t.key === 'notes' ? policy.notes.length : undefined;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn('flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors', active ? 'border-current' : 'border-transparent hover:opacity-80')}
              style={{ color: active ? 'var(--color-primary-600)' : 'var(--color-text-secondary)' }}
            >
              {t.label}
              {count !== undefined && (
                <span className="rounded-full px-1.5 py-0.5 text-[11px]" style={{ backgroundColor: 'var(--color-hover-background)', color: 'var(--color-text-secondary)' }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <SectionCard title="Insured">
              <DescriptionList
                items={[
                  { label: 'Named insured', value: policy.insured.name },
                  { label: 'Primary contact', value: policy.insured.contactName },
                  { label: 'Industry', value: policy.insured.industry },
                  { label: 'Employees', value: policy.insured.employeeCount.toLocaleString() },
                  { label: 'Email', value: policy.insured.email },
                  { label: 'Phone', value: policy.insured.phone },
                  {
                    label: 'Address',
                    value: (
                      <span className="inline-flex items-center gap-1.5">
                        <FaMapMarkerAlt className="h-3 w-3 flex-shrink-0" style={{ color: 'var(--color-text-secondary)' }} />
                        {`${policy.insured.address.street}, ${policy.insured.address.city}, ${policy.insured.address.state} ${policy.insured.address.zip}`}
                      </span>
                    ),
                  },
                  { label: 'Tax ID', value: policy.insured.taxId },
                ]}
              />
            </SectionCard>
            <SectionCard title="Distribution & Carrier">
              <DescriptionList
                items={[
                  { label: 'Producing broker', value: `${policy.broker.name}` },
                  { label: 'Agency', value: policy.broker.agency },
                  { label: 'Broker email', value: policy.broker.email },
                  { label: 'License #', value: policy.broker.licenseNo },
                  { label: 'Carrier', value: policy.carrier.name },
                  { label: 'A.M. Best', value: policy.carrier.amBestRating },
                ]}
              />
            </SectionCard>
          </div>
          <div className="space-y-6">
            <SectionCard title="Policy summary">
              <DescriptionList
                columns={1}
                items={[
                  { label: 'Status', value: <PolicyStatusBadge status={policy.status} /> },
                  { label: 'Policy period', value: `${formatDate(policy.effectiveDate)} – ${formatDate(policy.expirationDate)}` },
                  { label: 'Term', value: `${policy.termMonths} months` },
                  { label: 'Annual premium', value: formatCurrency(policy.annualPremium) },
                  { label: 'Created', value: `${formatDate(policy.createdAt)} (${relativeTime(policy.createdAt)})` },
                ]}
              />
            </SectionCard>
            {policy.costs && (
              <SectionCard title="Total Costs" description="Program costs carried over from the quote">
                <dl className="space-y-2.5">
                  {[
                    { label: 'Total Program Cost w/o Tax/Stamping/Issuance Fees', value: policy.costs.totalProgramCostWithoutFees },
                    { label: 'Tax/Stamping/Issuance Fees', value: policy.costs.taxStampingIssuanceFees },
                  ].map((row) => (
                    <div key={row.label} className="flex items-start justify-between gap-4 text-sm">
                      <dt style={{ color: 'var(--color-text-secondary)' }}>{row.label}</dt>
                      <dd className="flex-shrink-0 font-semibold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>{formatCurrency(row.value)}</dd>
                    </div>
                  ))}
                  <div
                    className="mt-1 flex items-start justify-between gap-4 rounded-lg border px-3 py-2.5 text-sm"
                    style={{ backgroundColor: 'var(--color-warning-50)', borderColor: 'var(--color-warning-100)' }}
                  >
                    <dt className="font-bold" style={{ color: 'var(--color-warning-800)' }}>Total Program Costs w/ Tax/Stamping/Issuance Fees</dt>
                    <dd className="flex-shrink-0 text-base font-bold tabular-nums" style={{ color: 'var(--color-warning-800)' }}>{formatCurrency(policy.costs.totalProgramCostWithFees)}</dd>
                  </div>
                </dl>
              </SectionCard>
            )}
          </div>
        </div>
      )}

      {tab === 'coverage' && (
        <div className="space-y-6">
          <SectionCard title="Coverage summary">
            <DescriptionList
              columns={3}
              items={[
                { label: 'Benefit level', value: `${policy.coverage.benefitPercent}% of wages` },
                { label: 'Weekly maximum', value: formatCurrency(policy.coverage.weeklyCap) },
                { label: 'Leave duration', value: `${policy.coverage.lengthOfLeaveWeeks} weeks` },
                { label: 'Waiting period', value: `${policy.coverage.waitingPeriodDays} days` },
                { label: 'Aggregate limit', value: formatCurrency(policy.coverage.aggregateLimit) },
                { label: 'Concurrency', value: policy.coverage.concurrency },
                { label: 'SDI offset', value: policy.coverage.sdiOffset ? 'Yes' : 'No' },
                { label: 'PFL offset', value: policy.coverage.pflOffset ? 'Yes' : 'No' },
              ]}
            />
          </SectionCard>
          <SectionCard title="Coverage lines" bodyClassName="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ color: 'var(--color-text-secondary)' }}>
                    {['Coverage', 'Benefit', 'Weekly cap', 'Leave', 'Waiting', 'Aggregate', 'Premium'].map((h, i) => (
                      <th key={h} className={cn('px-4 py-2.5 text-xs font-semibold uppercase tracking-wide', i > 0 ? 'text-right' : 'text-left')} style={{ borderBottom: '1px solid var(--color-border-light)' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {policy.coverageLines.map((line) => (
                    <tr key={line.id} style={{ borderBottom: '1px solid var(--color-border-light)' }}>
                      <td className="px-4 py-3 font-medium" style={{ color: 'var(--color-text-primary)' }}>{line.type}</td>
                      <td className="px-4 py-3 text-right" style={{ color: 'var(--color-text-primary)' }}>{line.benefitPercent}%</td>
                      <td className="px-4 py-3 text-right" style={{ color: 'var(--color-text-primary)' }}>{formatCurrency(line.weeklyCap)}</td>
                      <td className="px-4 py-3 text-right" style={{ color: 'var(--color-text-primary)' }}>{line.lengthOfLeaveWeeks} wks</td>
                      <td className="px-4 py-3 text-right" style={{ color: 'var(--color-text-primary)' }}>{line.waitingPeriodDays}d</td>
                      <td className="px-4 py-3 text-right" style={{ color: 'var(--color-text-primary)' }}>{formatCurrency(line.aggregateLimit)}</td>
                      <td className="px-4 py-3 text-right font-semibold" style={{ color: 'var(--color-text-primary)' }}>{formatCurrency(line.annualPremium)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </div>
      )}

      {tab === 'risk' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <SectionCard title="Risk assessment" className="lg:col-span-1">
            <div className="flex items-center gap-4">
              <div className="text-4xl font-black" style={{ color: 'var(--color-text-primary)' }}>
                {policy.risk.score}
                <span className="text-lg font-semibold" style={{ color: 'var(--color-text-secondary)' }}>/100</span>
              </div>
              <RiskTierBadge tier={policy.risk.tier} size="md" />
            </div>
            <ProgressBar className="mt-4" value={policy.risk.score} accent={policy.risk.score >= 80 ? 'success' : policy.risk.score >= 65 ? 'info' : 'warning'} />
            <div className="mt-4 flex items-center justify-between text-sm">
              <span style={{ color: 'var(--color-text-secondary)' }}>Projected loss ratio</span>
              <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{policy.risk.lossRatioPct}%</span>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{policy.risk.summary}</p>
          </SectionCard>
          <SectionCard title="Risk factors" className="lg:col-span-2" bodyClassName="p-0">
            <ul>
              {policy.risk.factors.map((factor) => (
                <li key={factor.name} className="flex items-center justify-between gap-4 border-b px-5 py-3.5 last:border-b-0" style={{ borderColor: 'var(--color-border-light)' }}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{factor.name}</p>
                    <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{factor.detail}</p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-3">
                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>weight {factor.weight}%</span>
                    <span className="text-xs font-bold capitalize" style={{ color: RATING_COLOR[factor.rating] }}>{factor.rating}</span>
                  </div>
                </li>
              ))}
            </ul>
          </SectionCard>
        </div>
      )}

      {tab === 'documents' && (
        <SectionCard title="Documents" description="Binder, policy schedule, certificate of insurance, and more" bodyClassName="p-0">
          {policy.documents.length === 0 ? (
            <EmptyState title="No documents yet" message="Documents are generated as the policy progresses through issuance." />
          ) : (
            <ul>
              {policy.documents.map((doc) => (
                <li key={doc.id} className="flex flex-col gap-3 border-b px-5 py-4 last:border-b-0 sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: 'var(--color-border-light)' }}>
                  <div className="flex min-w-0 items-center gap-3">
                    <DocumentTypeBadge type={doc.type} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{doc.name}</p>
                      <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                        v{doc.version} · {doc.pages} pages · {doc.sizeKb} KB · {doc.generatedBy} · {relativeTime(doc.generatedAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <Button size="sm" variant="ghost" leftIcon={<FaEye className="h-3 w-3" />} onClick={() => setPreviewDocId(doc.id)}>Preview</Button>
                    {doc.downloadUrl ? (
                      <Button size="sm" variant="ghost" leftIcon={<FaDownload className="h-3 w-3" />} onClick={() => triggerUrlDownload(doc.downloadUrl!)}>Download</Button>
                    ) : (
                      <Button size="sm" variant="ghost" disabled leftIcon={<FaSpinner className="h-3 w-3 animate-spin" />}>Generating…</Button>
                    )}
                    <Button size="sm" variant="ghost" leftIcon={<FaRedo className="h-3 w-3" />} loading={regenerating === doc.id} onClick={() => handleRegenerate(doc)}>Regenerate</Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      )}

      {tab === 'timeline' && (
        <SectionCard title="Audit timeline" description="Every action is recorded with a timestamp and user">
          <Timeline events={policy.timeline} />
        </SectionCard>
      )}

      {tab === 'notes' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <SectionCard title="Add note" className="lg:col-span-1">
            <div className="space-y-3">
              <Select
                label="Category"
                options={[
                  { value: 'general', label: 'General' },
                  { value: 'underwriting', label: 'Underwriting' },
                  { value: 'servicing', label: 'Servicing' },
                  { value: 'claims', label: 'Claims' },
                ]}
                value={noteCategory}
                onChange={(e) => setNoteCategory(e.target.value as PolicyNote['category'])}
                fullWidth
              />
              <Textarea label="Note" value={noteBody} onChange={(e) => setNoteBody(e.target.value)} rows={4} placeholder="Add a note to this policy…" fullWidth />
              <Button onClick={handleAddNote} loading={savingNote} disabled={!noteBody.trim()} fullWidth>Add note</Button>
            </div>
          </SectionCard>
          <div className="lg:col-span-2">
            <SectionCard title={`Notes (${policy.notes.length})`} bodyClassName="p-0">
              {sortedNotes.length === 0 ? (
                <EmptyState title="No notes yet" message="Add the first note for this policy." />
              ) : (
                <ul>
                  {sortedNotes.map((note) => (
                    <li key={note.id} className="border-b px-5 py-4 last:border-b-0" style={{ borderColor: 'var(--color-border-light)' }}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{note.author}</span>
                            <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase" style={{ backgroundColor: 'var(--color-hover-background)', color: 'var(--color-text-secondary)' }}>{note.category}</span>
                            {note.pinned && <FaThumbtack className="h-3 w-3" style={{ color: 'var(--color-secondary-600)' }} />}
                          </div>
                          <p className="mt-1.5 text-sm" style={{ color: 'var(--color-text-primary)' }}>{note.body}</p>
                          <p className="mt-1 text-xs" style={{ color: 'var(--color-text-secondary)' }}>{relativeTime(note.createdAt)}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => void setNotePinned(policy.id, note.id, !note.pinned).catch(() => {})}
                          aria-label={note.pinned ? 'Unpin note' : 'Pin note'}
                          className="flex-shrink-0 rounded p-1.5 hover:bg-[var(--color-hover-background)]"
                          style={{ color: note.pinned ? 'var(--color-secondary-600)' : 'var(--color-text-secondary)' }}
                        >
                          <FaThumbtack className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          </div>
        </div>
      )}

      <SlideOver
        isOpen={!!previewDoc}
        onClose={() => setPreviewDocId(null)}
        title={previewDoc ? DOCUMENT_TYPE_META[previewDoc.type].label : 'Document'}
        subtitle={previewDoc?.name}
        width="xl"
        footer={
          previewDoc &&
          (previewDoc.downloadUrl ? (
            <Button leftIcon={<FaDownload className="h-3 w-3" />} onClick={() => triggerUrlDownload(previewDoc.downloadUrl!)}>
              Download
            </Button>
          ) : (
            <Button disabled leftIcon={<FaSpinner className="h-3 w-3 animate-spin" />}>
              Generating…
            </Button>
          ))
        }
      >
        {previewDoc && (
          <div className="space-y-5">
            <PdfPreview doc={previewDoc} policy={policy} />
            <div>
              <h4 className="mb-2 text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>Version history</h4>
              <ul className="space-y-2">
                {previewDoc.versions.map((v) => (
                  <li key={v.version} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2" style={{ borderColor: 'var(--color-border-light)' }}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                        v{v.version}
                        <span className="ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase" style={{ backgroundColor: 'var(--color-hover-background)', color: 'var(--color-text-secondary)' }}>{v.status}</span>
                      </p>
                      <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{v.note}</p>
                    </div>
                    <span className="flex-shrink-0 text-xs" style={{ color: 'var(--color-text-secondary)' }}>{v.generatedBy} · {formatDate(v.generatedAt)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </SlideOver>
    </div>
  );
};
